/**
 * SiembraLoteRepository - Siembras de cada lote de siembra (`siembras_lote`)
 *
 * Implementación compartida web (Dexie) / mobile (SQLite); hoy sólo la web la registra
 * (mobile no sincroniza esta tabla en esta fase).
 *
 * Mismo modelo que PuntoControlRepository: delete() es SOFT-delete (marca
 * is_deleted=true y emite el UPDATE de sync) porque `siembras_lote` tiene la columna
 * is_deleted en Supabase, no tiene policy DELETE, y así los borrados se propagan
 * offline vía UPDATE.
 *
 * Serialización de `resumen`: en Supabase es JSONB y viaja por la cola de sync como
 * OBJETO, pero en los caches locales se guarda como STRING JSON (SQLite no soporta
 * objetos como valor de columna). Este repo normaliza en el límite, igual que
 * PuntoControlEventoRepository con sus campos JSON opcionales:
 *   - Escrituras (add/update/bulkPutSilent): stringifica `resumen` para el adapter
 *     local, pero emite el cambio de sync con el OBJETO.
 *   - Lecturas: parsea el string de vuelta a objeto (corrupto → null).
 *
 * Sync de los UPDATE: viajan con la fila COMPLETA (la local + los cambios), nunca con un
 * parcial. La cola de sync manda el UPDATE como upsert (INSERT … ON CONFLICT DO UPDATE) y
 * Postgres valida NOT NULL de la fila propuesta ANTES de resolver el conflicto: un parcial
 * `{id, fecha_fin, …}` falla offline por elemento_id / empresa_id / fecha_inicio. Aplica a
 * update(), softDelete()/delete() y restore().
 *
 * El orden de getByLote/getByEmpresa se resuelve en memoria: las siembras de un lote
 * son decenas, no miles, y `fecha_inicio` no es un índice del store local.
 */

import { v4 as uuidv4 } from 'uuid';
import {
  ISiembraLoteRepository,
  SiembraLote
} from '../interfaces/ISiembraLoteRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/** Campos JSON (objeto en Supabase, string en el caché local). */
const CAMPOS_JSON = ['resumen'] as const;

export class SiembraLoteRepository
  extends BaseRepository<SiembraLote, string>
  implements ISiembraLoteRepository
{
  /** Clause reutilizable para excluir soft-deletes. */
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'siembras_lote', 'siembras_lote');

    this.supportsSoftDelete = true;
    this.autoTimestamp = true;

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
  }

  // ==================== Normalización resumen ====================

  /** Campos JSON objeto → string JSON (para el adapter local). */
  private toStorage(record: Record<string, any>): Record<string, any> {
    const out = { ...record };
    for (const campo of CAMPOS_JSON) {
      if (out[campo] != null && typeof out[campo] !== 'string') out[campo] = JSON.stringify(out[campo]);
    }
    return out;
  }

  /** Campos JSON string del caché local → objeto (para los consumidores). */
  private fromStorage<T>(row: T): T {
    if (!row) return row;
    const out: Record<string, any> = { ...row };
    for (const campo of CAMPOS_JSON) {
      if (typeof out[campo] === 'string') {
        try {
          out[campo] = JSON.parse(out[campo]);
        } catch {
          out[campo] = null;
        }
      }
    }
    return out as T;
  }

  private fromStorageList(rows: any[]): any[] {
    return rows.map(r => this.fromStorage(r));
  }

  // ==================== CRUD ====================

  async add(data: Omit<SiembraLote, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: SiembraLote = {
      ...data,
      id,
      // La RLS de INSERT exige user_id = auth.uid(): sin perfil queda null y la DB lo rechaza
      // (mejor que un '' que ni siquiera es un uuid).
      user_id: data.user_id || profile?.user_id || null,
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      created_at: data.created_at || now,
      updated_at: now,
      is_deleted: false
    };

    // Local: string; sync: objeto (JSONB en Supabase).
    await this.db.insert('siembras_lote', this.toStorage(record));
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<SiembraLote> & { id: string }): Promise<void> {
    await this.aplicarCambios(data.id, { ...data, updated_at: this.getCurrentTimestamp() }, 'UPDATE');
  }

  /**
   * Soft-delete: marca is_deleted=true en lugar de borrar la fila. El UPDATE viaja
   * por la cola de sync y el borrado se propaga a Supabase (y al resto de los
   * dispositivos) aunque se haga offline.
   */
  async delete(id: string): Promise<void> {
    await this.softDelete(id);
  }

  /** Igual que BaseRepository.softDelete, pero el sync viaja con la fila completa. */
  async softDelete(id: string): Promise<void> {
    await this.aplicarCambios(id, { id, is_deleted: true, updated_at: this.getCurrentTimestamp() }, 'SOFT_DELETE');
  }

  /** Igual que BaseRepository.restore, pero el sync viaja con la fila completa. */
  async restore(id: string): Promise<void> {
    await this.aplicarCambios(id, { id, is_deleted: false, updated_at: this.getCurrentTimestamp() }, 'RESTORE');
  }

  /**
   * Escribe `cambios` en el caché local (sólo lo que cambió) y emite el sync con la fila
   * COMPLETA: la fila local actual + los cambios (ver la cabecera). Si la fila no está en el
   * caché no hay nada con qué completarla y se emite lo recibido.
   */
  private async aplicarCambios(
    id: string,
    cambios: Partial<SiembraLote> & { id: string },
    accion: 'UPDATE' | 'SOFT_DELETE' | 'RESTORE'
  ): Promise<void> {
    const actual = await this.db.get('siembras_lote', id);
    await this.db.update('siembras_lote', id, this.toStorage(cambios));
    const completa = actual ? { ...this.fromStorage(actual as any), ...cambios } : cambios;
    this.emitSync(accion, completa as SiembraLote);
  }

  /**
   * Override del PULL: las filas vienen de Supabase con `resumen` como objeto JSONB;
   * se stringifican antes de llegar al adapter local. El evento local (refresco de UI)
   * se emite con el objeto parseado y NO re-emite sync.
   */
  async bulkPutSilent(items: any[]): Promise<void> {
    await this.db.bulkUpsert('siembras_lote', items.map(i => this.toStorage(i)));
    items.forEach(item => {
      this.emitLocalSync('UPDATE', this.fromStorage(item));
    });
  }

  async get(id: string): Promise<SiembraLote | undefined> {
    const row = await this.db.get('siembras_lote', id);
    if (!row) return undefined;
    return this.fromStorage(row as any) as SiembraLote;
  }

  async getAll(): Promise<SiembraLote[]> {
    const rows = ((await this.db.getAll('siembras_lote')) as any[]) || [];
    // Chequeo falsy a propósito (patrón ElementoMapaRepository): en Dexie is_deleted
    // puede llegar como 0/1 (hooks de normalización).
    return this.fromStorageList(rows.filter(r => !r.is_deleted)) as SiembraLote[];
  }

  // ==================== Queries ====================

  /** La de inicio más reciente primero; a igual fecha, la cargada más tarde. */
  private ordenarPorInicioDesc(rows: SiembraLote[]): SiembraLote[] {
    return [...rows].sort((a, b) =>
      (b.fecha_inicio ?? '').localeCompare(a.fecha_inicio ?? '') ||
      (b.created_at ?? '').localeCompare(a.created_at ?? '')
    );
  }

  async getByLote(elementoId: string): Promise<SiembraLote[]> {
    const rows = await this.db.queryMultiple<SiembraLote>('siembras_lote', [
      this.NOT_DELETED,
      { field: 'elemento_id', operator: 'equals', value: elementoId }
    ]);
    return this.ordenarPorInicioDesc(this.fromStorageList(rows as any[]) as SiembraLote[]);
  }

  async getByEmpresa(empresaId: string): Promise<SiembraLote[]> {
    const rows = await this.db.queryMultiple<SiembraLote>('siembras_lote', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ]);
    return this.ordenarPorInicioDesc(this.fromStorageList(rows as any[]) as SiembraLote[]);
  }
}
